import {Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {Subject, takeUntil} from 'rxjs';
import {AppointmentDetailModel, ReservedAppointmentsModel} from '../../../models/appointment/appointment.model';
import {PatientService} from '../../../services/patient/patient.service';
import {AppointmentService} from '../../../services/appointment/appointment.service';
import {InfoMessageService} from '../../../services/infomessage/infomessage.service';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../confirm-dialog/confirm-dialog.component';
import {PatientModel} from '../../../models/patient/patient.model';

@Component({
    selector: 'app-appointment-details',
    standalone: true,
    imports: [CommonModule, DatePipe, RouterLink],
    templateUrl: './appointment-details.component.html',
    styleUrl: './appointment-details.component.sass'
})
export class AppointmentDetailsComponent implements OnInit, OnDestroy {
    private destroy$: Subject<void> = new Subject<void>();
    appointment: ReservedAppointmentsModel | null = null;
    patient: PatientModel | null = null;
    isLoading = true;
    isPast = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private patientService: PatientService,
        private appointmentService: AppointmentService,
        private infoMessageService: InfoMessageService,
        private dialog: MatDialog
    ) {
        const navigation = this.router.getCurrentNavigation();
        if (navigation?.extras?.state?.['appointment']) {
            this.appointment = navigation.extras.state['appointment'];
            this.checkIfPast();
            this.isLoading = false;
        }
    }

    ngOnInit() {
        if (!this.appointment) {
            this.loadAppointment();
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadAppointment() {
        const id = this.route.snapshot.paramMap.get('id');
        if (!id) {
            this.router.navigate(['/termine']);
            return;
        }

        // Da es keinen direkten Endpoint für einen einzelnen Termin gibt (laut PatientService),
        // laden wir alle Termine und filtern.
        this.patientService.getMe()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.patient = response.data;
                    this.patientService.getPatientAppointments(response.data.id)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                            next: (appointmentsResponse) => {
                                const found = appointmentsResponse.data.find((appointment: ReservedAppointmentsModel) => appointment.id === id);
                                if (found) {
                                    this.appointment = found;
                                    console.log('found appointment:', this.appointment);
                                    this.checkIfPast();
                                } else {
                                    console.error('appointment not found in list', appointmentsResponse.data);
                                    this.infoMessageService.createMessage('Termin nicht gefunden', 'error');
                                    this.router.navigate(['/termine']);
                                }
                                this.isLoading = false;
                            },
                            error: () => {
                                this.isLoading = false;
                                this.router.navigate(['/termine']);
                            }
                        });
                },
                error: () => {
                    this.isLoading = false;
                    this.router.navigate(['/termine']);
                }
            });
    }

    private checkIfPast() {
        if (this.appointment) {
            this.isPast = new Date(this.appointment.from) < new Date();
        }
    }

    cancelAppointment() {
        if (!this.appointment || this.isPast) return;

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                header: 'Termin absagen',
                text: 'Möchten Sie diesen Termin wirklich absagen?',
                delete: true
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && this.appointment) {
                this.appointmentService.patientCancelAppointment(this.appointment.id, this.patient!.id || '', 'Vom Patienten abgesagt')
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            this.infoMessageService.createMessage('Termin wurde erfolgreich abgesagt', 'success');
                            this.router.navigate(['/termine']);
                        },
                        error: () => {
                            this.infoMessageService.createMessage('Fehler beim Absagen des Termins', 'error');
                        }
                    });
            }
        });

    }
}
